/**
 * Web Push subscription helpers.
 * Register + persist a browser push subscription against the current family/user,
 * or unsubscribe cleanly.
 *
 * The public VAPID key is baked at build time via VITE_VAPID_PUBLIC_KEY.
 * The private key never leaves the send-push edge function.
 */

import { supabase } from './supabase'

const VAPID_PUBLIC = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined

export function pushSupported(): boolean {
  return typeof window !== 'undefined'
    && 'Notification' in window
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && !!VAPID_PUBLIC
}

export type PushPermission = 'default' | 'granted' | 'denied' | 'unsupported'

export function pushPermission(): PushPermission {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported'
  return Notification.permission as PushPermission
}

export async function isSubscribed(): Promise<boolean> {
  if (!pushSupported()) return false
  const reg = await navigator.serviceWorker.getRegistration()
  if (!reg) return false
  const sub = await reg.pushManager.getSubscription()
  return !!sub
}

/**
 * Full subscribe flow:
 *   1. Request notification permission (if not granted)
 *   2. Get PushManager subscription (or reuse existing)
 *   3. UPSERT into public.push_subscriptions
 * Returns true on success, throws on any failure.
 */
export async function subscribePush(params: { familyId: string; userId: string }): Promise<boolean> {
  if (!pushSupported() || !VAPID_PUBLIC) throw new Error('Web Push not supported in this browser')

  // Ensure permission
  if (Notification.permission === 'default') {
    const result = await Notification.requestPermission()
    if (result !== 'granted') throw new Error('Notification permission denied')
  } else if (Notification.permission === 'denied') {
    throw new Error('Notifications are blocked. Enable them in browser settings and try again.')
  }

  const reg = await navigator.serviceWorker.ready

  // Reuse existing subscription if the browser already has one for this SW
  let sub = await reg.pushManager.getSubscription()
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC) as BufferSource,
    })
  }

  const json = sub.toJSON()
  const endpoint = json.endpoint
  const p256dh = json.keys?.p256dh
  const auth = json.keys?.auth
  if (!endpoint || !p256dh || !auth) throw new Error('Invalid PushSubscription payload')

  const { error } = await supabase
    .from('push_subscriptions')
    .upsert(
      {
        user_id: params.userId,
        family_id: params.familyId,
        endpoint,
        p256dh,
        auth,
        user_agent: navigator.userAgent,
      },
      { onConflict: 'user_id,endpoint' },
    )
  if (error) throw new Error(`Failed to save subscription: ${error.message}`)

  return true
}

/** Unsubscribe from browser Push + remove the row from Supabase. */
export async function unsubscribePush(): Promise<void> {
  if (!pushSupported()) return
  const reg = await navigator.serviceWorker.getRegistration()
  if (!reg) return
  const sub = await reg.pushManager.getSubscription()
  if (!sub) return
  const endpoint = sub.endpoint
  await sub.unsubscribe()
  await supabase.from('push_subscriptions').delete().eq('endpoint', endpoint)
}

/**
 * Fire a push notification via the send-push edge function.
 * Used from emitEvent hook to notify other family members when the caller
 * logs an event.
 *
 * exclude_user_id defaults to caller — send-push sees that on the JWT and
 * excludes automatically, but pass it explicitly for tracing clarity.
 */
export async function firePush(params: {
  familyId: string
  excludeUserId?: string
  title: string
  body: string
  url?: string
  tag?: string
  requirePref?: 'partner_events' | 'feed_reminders' | 'needs'
}): Promise<void> {
  try {
    await supabase.functions.invoke('send-push', {
      body: {
        family_id: params.familyId,
        exclude_user_id: params.excludeUserId,
        title: params.title,
        body: params.body,
        url: params.url,
        tag: params.tag,
        require_pref: params.requirePref,
      },
    })
  } catch (e) {
    // Non-fatal — push is best-effort. Don't block the caller's UI on push failure.
    // eslint-disable-next-line no-console
    console.warn('[BabyOS] send-push failed', e)
  }
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const s = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(s)
  const out = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}
